import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Table,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchStudentFeeStructure, fetchInvoices } from "../../../api/invoiceApi";
import numberToWords from "../../Accounts/utils/numberToWords";
import axios from "axios";

export const getAcademicSessionForIndex = (baseYear, index) => {
  if (!baseYear) return `Academic Session [${2025 + index}-${(2026 + index).toString().slice(-2)}]`;
  
  const match = baseYear.match(/(\d{4})[\/-](\d{2,4})/);
  if (match) {
    const startYear = parseInt(match[1], 10) + index;
    const endYearVal = parseInt(match[2], 10);
    const isFourDigitEnd = match[2].length === 4;

    let nextEndYear;
    if (isFourDigitEnd) {
      nextEndYear = endYearVal + index;
    } else {
      const fullEndYear = Math.floor(parseInt(match[1], 10) / 100) * 100 + endYearVal;
      nextEndYear = (fullEndYear + index).toString().slice(-2);
    }
    return `Academic Session [${startYear}-${nextEndYear}]`;
  }

  const singleYear = parseInt(baseYear, 10);
  if (!isNaN(singleYear)) {
    const start = singleYear + index;
    const end = (start + 1).toString().slice(-2);
    return `Academic Session [${start}-${end}]`;
  }

  return `Academic Session [${baseYear}]`;
};

const FeeDefaultNoticeLetter = ({ student }) => {
  const date = new Date();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.authentication);
  const { userData, branchData } = auth;
  const logoUrl = branchData?.logoUrl || "";
  const [invoiceData, setInvoiceData] = useState([]);

  const [formData, setFormData] = useState({
    studentName: "",
    fatherName: "",
    address: "",
    course: "",
    academicYear: "",
    admissionNo: "",
    fees: [],
    lastDateWithoutFine: "",
    lastDateWithFine: "",
  });

  const printRef = useRef(null);

  const semesterToYearMap = {
    1: 1, 2: 1,
    3: 2, 4: 2,
    5: 3, 6: 3,
    7: 4, 8: 4,
  };

  const ordinalWordToNumber = {
    first: "1st", second: "2nd", third: "3rd", fourth: "4th",
    fifth: "5th", sixth: "6th", seventh: "7th", eighth: "8th",
  };

  // Helper to generate sequential Academic Session string based on base year range and index
 // const getAcademicSessionForIndex = (baseYear, index) => {
 //   if (!baseYear) return `Academic Session [${2025 + index}-${(2026 + index).toString().slice(-2)}]`;
 //   
 //   // Matches formats like "2025-26", "2025-2026", "2025/26"
 //   const match = baseYear.match(/(\d{4})[\/-](\d{2,4})/);
 //   if (match) {
 //     const startYear = parseInt(match[1], 10) + index;
 //     const endYearVal = parseInt(match[2], 10);
 //     const isFourDigitEnd = match[2].length === 4;
//
 //     let nextEndYear;
 //     if (isFourDigitEnd) {
 //       nextEndYear = endYearVal + index;
 //     } else {
 //       const fullEndYear = Math.floor(parseInt(match[1], 10) / 100) * 100 + endYearVal;
 //       nextEndYear = (fullEndYear + index).toString().slice(-2);
 //     }
 //     return `Academic Session [${startYear}-${nextEndYear}]`;
 //   }
//
 //   // Fallback if baseYear is single year e.g. "2025"
 //   const singleYear = parseInt(baseYear, 10);
 //   if (!isNaN(singleYear)) {
 //     const start = singleYear + index;
 //     const end = (start + 1).toString().slice(-2);
 //     return `Academic Session [${start}-${end}]`;
 //   }
//
 //   return `Academic Session [${baseYear}]`;
 // };
 // Add 'export' before 'const'


  const getPeriodFromDescription = (description) => {
    if (!description) return { type: null, value: null, yearFallback: null };
    let lower = description.toLowerCase();
    lower = lower.replace(/(\d+)(st|nd|rd|th)?(year|sem|semester)/g, "$1$2 $3");

    for (const [word, ordinal] of Object.entries(ordinalWordToNumber)) {
      const regex = new RegExp(`\\b${word}\\b`, "g");
      lower = lower.replace(regex, ordinal);
    }

    const semMatch = lower.match(/\b(\d+)(st|nd|rd|th)?\s*(sem|semester)/);
    if (semMatch) {
      const semNumber = parseInt(semMatch[1], 10);
      return {
        type: "semester",
        value: semNumber,
        yearFallback: semesterToYearMap[semNumber] || null,
      };
    }

    const yearMatch = lower.match(/\b(\d+)(st|nd|rd|th)?\s*year/);
    if (yearMatch) {
      const yearNumber = parseInt(yearMatch[1], 10);
      return { type: "year", value: yearNumber, yearFallback: yearNumber };
    }

    return { type: null, value: null, yearFallback: null };
  };

  const getStudentFeeStructureData = async (studentObj) => {
    try {
      const res = await fetchStudentFeeStructure({
        branch: userData.branch,
        studentId: studentObj.studentId,
        token: userData.token,
        navigate,
      });

      const { data, fees, error } = res;

      const baseStudentName = [studentObj.firstName, studentObj.middleName, studentObj.lastName].filter(Boolean).join(" ");
      const defaultAcadYear = studentObj.academicYear || data?.academicYear || "2025-26";

      if (error) {
        setFormData((prev) => ({
          ...prev,
          studentName: baseStudentName,
          fatherName: studentObj.fatherName || "",
          address: studentObj.homeAddress || "",
          course: studentObj.selectedCourse || "",
          academicYear: defaultAcadYear,
          admissionNo: studentObj.studentId || "",
          fees: [
            { description: "1st Year Fee", academicPeriod: getAcademicSessionForIndex(defaultAcadYear, 0), amount: 0 },
            { description: "2nd Year Fee", academicPeriod: getAcademicSessionForIndex(defaultAcadYear, 1), amount: 0 },
          ],
        }));
        return;
      }

      const feeItems = fees && fees.length > 0 
        ? fees.map((f, idx) => ({ 
            description: f.description, 
            academicPeriod: f.academicPeriod || getAcademicSessionForIndex(defaultAcadYear, idx), 
            amount: f.amount || 0 
          }))
        : [
            { description: "1st Year Fee", academicPeriod: getAcademicSessionForIndex(defaultAcadYear, 0), amount: 0 },
            { description: "2nd Year Fee", academicPeriod: getAcademicSessionForIndex(defaultAcadYear, 1), amount: 0 },
          ];

      setFormData((prev) => ({
        ...prev,
        ...(data || {}),
        ...(data?.feeLetter || {}),
        studentName: baseStudentName || data?.studentName || "",
        fatherName: studentObj.fatherName || data?.fatherName || "",
        address: studentObj.homeAddress || data?.address || "",
        course: studentObj.selectedCourse || data?.course || "",
        academicYear: defaultAcadYear,
        admissionNo: studentObj.studentId || data?.admissionNo || "",
        fees: feeItems,
      }));
    } catch (err) {
      console.error("Error fetching fee structure:", err);
    }
  };

 useEffect(() => {
    if (student) {
      // 1. Handle student fee data instantly if fields exist, otherwise fetch them
      if (student.fatherName && student.homeAddress && student.selectedCourse) {
        getStudentFeeStructureData(student);
      } else {
        const fetchMissingStudentData = async () => {
          try {
            const response = await axios.get(`${process.env.REACT_APP_COLLEGE_ERP_API}api/studentId`, {
              params: {
                studentId: student.studentId,
                branch: userData?.branch,
              },
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${userData?.token}`,
              },
              
            })
            getStudentFeeStructureData(response.data);
          } catch (error) {
            console.error("Error fetching student details:", error);
            // Fallback to existing student data if the API fails
            getStudentFeeStructureData(student);
          }
        };
        fetchMissingStudentData();
      }

      // 2. Load invoices concurrently (runs immediately without waiting for the API check)
      const loadInvoices = async () => {
        const resData = await fetchInvoices({
          branch: userData.branch,
          studentId: student.studentId,
          token: userData.token,
          navigate,
        });
        setInvoiceData(resData || []);
      };
      loadInvoices();
    }
  }, [student]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFeeChange = (index, field, value) => {
    const updatedFees = [...formData.fees];
    updatedFees[index][field] = value;
    setFormData((prev) => ({ ...prev, fees: updatedFees }));
  };

  const addFeeRow = () => {
    const defaultAcadYear = formData.academicYear || "2025-26";
    const nextIndex = formData.fees.length;
    setFormData((prev) => ({
      ...prev,
      fees: [
        ...prev.fees, 
        { 
          description: "", 
          academicPeriod: getAcademicSessionForIndex(defaultAcadYear, nextIndex), 
          amount: "" 
        }
      ],
    }));
  };

  const removeFeeRow = (index) => {
    const updatedFees = formData.fees.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, fees: updatedFees }));
  };

  const getProcessedFees = () => {
    return (formData.fees || []).map((fee) => {
      const currentPeriod = getPeriodFromDescription(fee.description);
      let paidAmount = 0;

      invoiceData?.forEach((invoice) => {
        (invoice?.fees || []).forEach((f) => {
          const invPeriod = getPeriodFromDescription(f.description);
          if (!currentPeriod.type || !invPeriod.type) return;

          let isMatch = false;
          if (currentPeriod.type === invPeriod.type) {
            isMatch = invPeriod.value === currentPeriod.value;
          } else if (currentPeriod.type === "year" && invPeriod.type === "semester") {
            isMatch = invPeriod.yearFallback === currentPeriod.value;
          } else if (currentPeriod.type === "semester" && invPeriod.type === "year") {
            isMatch = currentPeriod.yearFallback === invPeriod.value;
          }

          if (isMatch) {
            paidAmount += Number(f.amount || 0);
          }
        });
      });

      Object.keys(formData).forEach((key) => {
        const keyLower = key.toLowerCase();
        if (!keyLower.includes("paid")) return;
        if (key === "totalPaid" || key === "admissionFeePaid") return;

        const keyPeriod = getPeriodFromDescription(key);
        if (!keyPeriod.type) return;

        let isMatch = false;
        if (currentPeriod.type === keyPeriod.type) {
          isMatch = keyPeriod.value === currentPeriod.value;
        } else if (currentPeriod.type === "year" && keyPeriod.type === "semester") {
          isMatch = keyPeriod.yearFallback === currentPeriod.value;
        } else if (currentPeriod.type === "semester" && keyPeriod.type === "year") {
          isMatch = currentPeriod.yearFallback === keyPeriod.value;
        }

        if (isMatch) {
          paidAmount += Number(formData[key] || 0);
        }
      });

      const totalAmount = Number(fee.amount || 0);
      const balanceAmount = Math.max(0, totalAmount - paidAmount);

      return {
        ...fee,
        paidAmount,
        balanceAmount,
      };
    });
  };

  const processedFees = getProcessedFees();
  const totalBalanceSum = processedFees.reduce((acc, curr) => acc + curr.balanceAmount, 0);

  const generateNoticeNo = (serialNumber) => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const month = today.getMonth() + 1;

    let fy;
    if (month >= 4) {
      fy = `${currentYear}-${(currentYear + 1).toString().slice(-2)}`;
    } else {
      fy = `${currentYear - 1}-${currentYear.toString().slice(-2)}`;
    }

    const paddedSerial = String(serialNumber).padStart(3, "0");
    return `RIN/FIN/${fy}/${formData.admissionNo || paddedSerial}`;
  };

  const noticeNo = generateNoticeNo(1);

  const handlePrint = () => {
    const printContent = printRef.current ? printRef.current.innerHTML : "";
    const printWindow = window.open("", "_blank", "height=800,width=900");

    if (printWindow) {
      printWindow.document.write(`
        <html>
        <head>
          <title>Print Notice</title>
          <style>
            @page { 
              size: A4; 
              margin: 15mm; 
            }
            body { 
              font-family: 'Helvetica Neue', Arial, sans-serif; 
              margin: 0; 
              padding: 0;
              line-height: 1.4; 
              color: #2c3e50; 
              font-size: 11px; 
              -webkit-print-color-adjust: exact; 
              print-color-adjust: exact; 
            }
            .notice-container { 
              padding: 0; 
              width: 100%; 
              box-sizing: border-box; 
            }
            
            /* Header Table for Left-Aligned Logo */
            .header-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 8px;
            }
            .header-table td {
              border: none;
              vertical-align: middle;
            }
            .inst-title {
              font-size: 18px;
              font-weight: 800;
              color: #800000;
              letter-spacing: 0.5px;
              margin: 0 0 2px 0;
              text-transform: uppercase;
            }
            .inst-subtitle {
              font-size: 9.5px;
              color: #444;
              margin-bottom: 2px;
            }
            .inst-address {
              font-size: 9.5px;
              color: #555;
            }

            .header-divider {
              border-top: 1.5px solid #800000;
              margin: 8px 0 12px 0;
            }

            /* Title Banner */
            .title-banner {
              text-align: center;
              font-weight: 700;
              font-size: 12px;
              color: #800000;
              background-color: #fcf0f2;
              border-left: 4px solid #800000;
              padding: 6px 10px;
              margin-bottom: 14px;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }

            /* Student Info Table */
            .info-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 12px;
            }
            .info-table td {
              padding: 3px 0;
              vertical-align: top;
              font-size: 11px;
            }
            .info-label {
              font-weight: 700;
              color: #333;
              width: 15%;
            }
            .info-val {
              color: #111;
              width: 35%;
            }

            /* Subject Box */
            .subject-box {
              background-color: #f4f4f6;
              padding: 6px 10px;
              font-weight: 700;
              font-size: 11px;
              color: #222;
              margin-bottom: 10px;
              border-radius: 2px;
              text-transform: uppercase;
            }

            /* Paragraphs */
            p.salutation {
              font-weight: 600;
              margin: 0 0 6px 0;
            }
            p.body-text {
              margin: 0 0 10px 0;
              text-align: justify;
              color: #333;
              font-size: 10.5px;
            }

            /* Fee Table */
            table.content-table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 10px 0 6px 0; 
            }
            table.content-table th { 
              background-color: #a38288; 
              color: #ffffff; 
              font-weight: 600; 
              text-align: left; 
              padding: 6px 8px; 
              font-size: 10.5px; 
              border: 1px solid #a38288;
            }
            table.content-table td { 
              border: 1px solid #e0e0e0; 
              padding: 6px 8px; 
              font-size: 10.5px; 
            }
            table.content-table tr:nth-child(even) {
              background-color: #fafafa;
            }
            .amount-col {
              text-align: right;
              font-weight: 600;
            }
            .total-row td {
              border-top: 1.5px solid #800000 !important;
              font-weight: 700;
              background-color: #fdf5f5 !important;
              color: #800000;
            }
            .words-row td {
              font-style: italic;
              background-color: #fff !important;
              font-size: 10px;
              color: #444;
              padding: 4px 8px;
            }

            /* Fine Dates Box */
            .dates-box {
              border: 1px dashed #800000;
              background-color: #fff9f9;
              padding: 8px 10px;
              margin: 10px 0;
              border-radius: 4px;
            }
            .dates-title {
              font-weight: 700;
              color: #800000;
              margin-bottom: 4px;
              font-size: 11px;
            }

            /* Statutory Notice Box */
            .statutory-box {
              border: 1px solid #e6b8be;
              background-color: #fdf5f6;
              padding: 8px 10px;
              margin: 12px 0;
              border-radius: 4px;
            }
            .statutory-title {
              font-weight: 700;
              color: #800000;
              font-size: 11px;
              margin-bottom: 6px;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            .statutory-box ul {
              margin: 0;
              padding-left: 16px;
            }
            .statutory-box li {
              margin-bottom: 4px;
              font-size: 10px;
              color: #333;
            }

            /* Terms Declaration Box */
            .declaration-box { 
              border: 1px solid #d0d0d0; 
              padding: 8px 10px; 
              margin-top: 10px; 
              background-color: #ffffff; 
              border-radius: 4px;
            }
            .declaration-title {
              font-weight: 700;
              text-align: center;
              color: #222;
              margin-bottom: 6px;
              font-size: 10.5px;
            }
            .checkbox-item {
              display: flex;
              align-items: flex-start;
              font-size: 9.5px;
              margin-bottom: 4px;
              color: #333;
            }
            .checkbox-item span.box {
              display: inline-block;
              width: 10px;
              height: 10px;
              border: 1px solid #555;
              margin-right: 6px;
              margin-top: 1px;
              flex-shrink: 0;
            }

            /* Signatures Table */
            .sig-table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 12px; 
            }
            .sig-table td { 
              border: none; 
              padding: 4px 0; 
              font-size: 10px; 
            }

            /* Bank Details Box */
            .bank-box {
              border: 1px solid #dcdcdc;
              background-color: #fbfbfb;
              padding: 8px 10px;
              margin-top: 10px;
              border-radius: 4px;
            }
            .bank-title {
              font-weight: 700;
              color: #333;
              font-size: 10.5px;
              margin-bottom: 6px;
              text-transform: uppercase;
            }

            /* Footer Office Use */
            .office-box {
              border: 1px solid #333;
              padding: 6px 8px;
              margin-top: 10px;
              font-size: 10px;
            }

            .footer-note {
              font-size: 8.5px;
              color: #777;
              margin-top: 8px;
              display: flex;
              justify-content: space-between;
              border-top: 1px solid #eee;
              padding-top: 4px;
            }
          </style>
        </head>
        <body onload="window.print(); window.onafterprint = function() { window.close(); }">
          ${printContent}
        </body>
        </html>
      `);
      printWindow.document.close();
    } else {
      alert("Pop-ups are blocked! Please allow pop-ups for printing.");
    }
  };

  return (
    <Container className="py-4">
      <h3>Individual Fee Dues Notice & Declaration Generator</h3>
      <Form className="mb-4">
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Student Name</Label>
              <Input name="studentName" value={formData.studentName} onChange={handleChange} />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>Admission No</Label>
              <Input name="admissionNo" value={formData.admissionNo} onChange={handleChange} />
            </FormGroup>
          </Col>
        </Row>

        <Row className="bg-light p-3 border rounded mb-3">
          <Col md={6}>
           <FormGroup>
  <Label className="text-danger">
    <strong>Last Date for Fee Payment without Fine:</strong>
  </Label>
  <Input
    type="date"
    name="lastDateWithoutFine"
    value={formData.lastDateWithoutFine || "2026-10-28"}
    onChange={handleChange}
  />
</FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label className="text-danger"><strong>Last Date for Fee Payment with Fine:</strong></Label>
              <Input
                type="date"
                name="lastDateWithFine"
                value={formData.lastDateWithFine || "2026-11-06"}
                onChange={handleChange}
              />
            </FormGroup>
          </Col>
        </Row>

        <h5 className="mt-4">Fee Breakdown & Computed Balances</h5>
        <Table bordered responsive>
          <thead>
            <tr>
              <th style={{ width: "30%" }}>Fee Description</th>
              {
          //    <th style={{ width: "25%" }}>Academic Period / Session</th>
}
              <th style={{ width: "15%" }}>Total Amount (₹)</th>
              <th style={{ width: "12%" }}>Paid (₹)</th>
              <th style={{ width: "13%" }}>Balance Due (₹)</th>
              <th style={{ width: "5%" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {processedFees.map((fee, index) => (
              <tr key={index}>
                <td>
                  <Input
                    type="text"
                    value={fee.description}
                    onChange={(e) => handleFeeChange(index, "description", e.target.value)}
                    placeholder="e.g. Tuition Fee"
                  />
                </td>
                {
          //    <td>
          //      <Input
          //        type="text"
          //        value={fee.academicPeriod || ""}
          //        onChange={(e) => handleFeeChange(index, "academicPeriod", e.target.value)}
          //        placeholder="e.g. Academic Session [2025-26] or Sem 2"
        //          />
      //            </td>
}
                <td>
                  <Input
                    type="number"
                    value={fee.amount}
                    onChange={(e) => handleFeeChange(index, "amount", e.target.value)}
                  />
                </td>
                <td>
                  <Input type="number" value={fee.paidAmount} readOnly />
                </td>
                <td>
                  <Input type="number" value={fee.balanceAmount} readOnly style={{ fontWeight: "bold", backgroundColor: "#fff3cd" }} />
                </td>
                <td>
                  <Button color="danger" size="sm" onClick={() => removeFeeRow(index)}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Button color="secondary" size="sm" onClick={addFeeRow} className="mb-3">
          + Add Fee Row
        </Button>

        <br />
        <Button color="danger" onClick={handlePrint}>
          Print Official Notice & Declaration PDF
        </Button>
      </Form>

      {/* Printable Area */}
      <div style={{ display: "none" }}>
        <div ref={printRef} className="notice-container">
          
          {/* Header - Left-Aligned Logo */}
          <table className="header-table">
            <tbody>
              <tr>
                {logoUrl && (
                  <td style={{ width: "70px", paddingRight: "12px" }}>
                    <img
                      src={logoUrl}
                      alt="Institute Logo"
                      style={{
                        maxHeight: "65px",
                        maxWidth: "65px",
                        objectFit: "contain",
                      }}
                    />
                  </td>
                )}
                <td>
                  <h1 className="inst-title">Rehoboth Institute of Nursing</h1>
                  <div className="inst-subtitle">Affiliated to Rajiv Gandhi University of Health Sciences, Karnataka & Recognized by KNC & INC, New Delhi</div>
                  <div className="inst-address">No 164, Tawakkal Building, New Airport Road, Kadusonnapanahalli, Karnataka 560077</div>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="header-divider"></div>

          {/* Banner Title */}
          <div className="title-banner">
            Official Notice: Outstanding Fee Dues & Exam Restriction
          </div>

          {/* Info Section */}
          <table className="info-table">
            <tbody>
              <tr>
                <td className="info-label">Ref No:</td>
                <td className="info-val">{noticeNo}</td>
                <td className="info-label">Date:</td>
                <td className="info-val">{date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</td>
              </tr>
              <tr>
                <td className="info-label">Student Name:</td>
                <td className="info-val"><strong>{formData.studentName || "[Student Name]"}</strong></td>
                <td className="info-label">Admission No:</td>
                <td className="info-val">{formData.admissionNo || "[Admission No]"}</td>
              </tr>
              <tr>
                <td className="info-label">Father's Name:</td>
                <td className="info-val">{formData.fatherName || "[Father's Name]"}</td>
                <td className="info-label">Academic Year:</td>
                <td className="info-val">{formData.academicYear || "[Academic Year]"}</td>
              </tr>
              <tr>
                <td className="info-label">Course:</td>
                <td className="info-val">{formData.course || "[Course Name]"}</td>
                <td className="info-label">Address:</td>
                <td className="info-val">{formData.address || "[Student Address Details]"}</td>
              </tr>
            </tbody>
          </table>

          {/* Subject Box */}
          <div className="subject-box">
            Subject: Final Demand Notice for Overdue Fees and Restriction from Examinations
          </div>

          {/* Body Text */}
          <p className="salutation">Dear Student / Parent / Guardian,</p>
          <p className="body-text">
            This is an official communication from the Finance and Accounts Section of Rehoboth Institute of Nursing. According to the institute’s fee records, the following amount remains outstanding against your account. Please verify the details and clear the amount by the applicable deadline below. If you have already paid any amount shown, submit the receipt/transaction reference to the accounts section promptly so records can be checked and updated.
          </p>

          {/* Fee Table */}
          <table className="content-table">
            <thead>
              <tr>
                <th style={{ width: "45%" }}>Fee Description</th>
               {

                 // <th style={{ width: "30%" }}>Academic Period</th>
                }
                <th style={{ width: "25%", textAlign: "right" }}>Amount Due (INR)</th>
              </tr>
            </thead>
            <tbody>
              {processedFees.map((fee, idx) => (
                <tr key={idx}>
                  <td>{fee.description || "Fee Item"}</td>
            {
            //    <td>{fee.academicPeriod || getAcademicSessionForIndex(formData.academicYear, idx)}</td>
}
                  <td >
                    Rs. {fee.balanceAmount.toLocaleString("en-IN")}
                  </td>
                </tr>
              ))}
              <tr className="total-row">
                <td  style={{ textAlign: "right" }}>TOTAL OVERDUE BALANCE DEMANDED:</td>
                <td >Rs. {totalBalanceSum.toLocaleString("en-IN")} /-</td>
              </tr>
              <tr className="words-row">
                <td colSpan="2">
                  <strong>Amount in Words:</strong> Rupees {totalBalanceSum > 0 ? numberToWords(totalBalanceSum) : "Zero"} Only.
                </td>
              </tr>
            </tbody>
          </table>

          {/* Fee Payment Deadlines */}
          {(formData.lastDateWithoutFine || formData.lastDateWithFine) && (
            <div className="dates-box">
              <div className="dates-title">FEE PAYMENT DEADLINES</div>
              <div><strong>Last Date for Fee Payment without Fine:</strong> {formData.lastDateWithoutFine ? new Date(formData.lastDateWithoutFine).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) : "____________________"}</div>
              <div><strong>Last Date for Fee Payment with Fine:</strong> {formData.lastDateWithFine ? new Date(formData.lastDateWithFine).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) : "____________________"}</div>
            </div>
          )}

          {/* Statutory Academic Notice */}
          <div className="statutory-box">
            <div className="statutory-title">Statutory Academic Notice: Examination Eligibility Bar</div>
            <ul>
              <li><strong>Hall Ticket Withholding:</strong> Official Examination Hall Tickets / Admit Cards will <strong>NOT</strong> be generated or issued to students with pending financial dues.</li>
              <li><strong>Examination Bar:</strong> As per institutional regulations, students will <strong>NOT</strong> be permitted to sit for upcoming University / Internal Examinations without clearing total dues.</li>
              <li><strong>Academic Portal Lock:</strong> Access to institutional portals, internal assessment records, and practical evaluation sessions will remain suspended until full clearance.</li>
            </ul>
          </div>

          {/* Terms & Student Declaration */}
          <div className="declaration-box">
            <div className="declaration-title">TERMS AND CONDITIONS & STUDENT DECLARATION</div>
            
            <div className="checkbox-item">
              <span className="box"></span>
              <div>I confirm that I have read and understood the fee payment terms and conditions communicated by the institute and agree to abide by them, subject to applicable rules and regulations.</div>
            </div>
            <div className="checkbox-item">
              <span className="box"></span>
              <div>I understand that outstanding semester-wise or yearly fee dues may affect fee clearance and related administrative processing.</div>
            </div>
            <div className="checkbox-item">
              <span className="box"></span>
              <div>I understand that if the outstanding dues are not cleared, the No Objection Certificate (NOC) will not be issued, and the University examination application may not be generated or processed through the college system.</div>
            </div>
            <div className="checkbox-item">
              <span className="box"></span>
              <div>I have read and understood this notice and agree to abide by the applicable fee payment terms and conditions.</div>
            </div>
            <div className="checkbox-item">
              <span className="box"></span>
              <div>I understand the fee payment deadlines and that examination-related procedures are subjected to the institution's rules and regulations.</div>
            </div>

            <table className="sig-table">
              <tbody>
                <tr>
                  <td style={{ width: "35%" }}><strong>Student Name:</strong> {formData.studentName}</td>
                  <td style={{ width: "35%" }}><strong>Student Signature:</strong> _________________</td>
                  <td style={{ width: "30%", textAlign: "right" }}><strong>Date:</strong> ____________</td>
                </tr>
                <tr>
                  <td colSpan="3" style={{ paddingTop: "8px" }}>
                    <strong>Parent/Guardian Name & Signature:</strong> _______________________________________________
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Bank Remittance Details */}
          <div className="bank-box">
            <div className="bank-title">Official Bank Remittance Details (NEFT / RTGS / IMPS)</div>
            <table style={{ width: "100%", fontSize: "10px" }}>
              <tbody>
                <tr>
                  <td><strong>Account Name:</strong> Rehoboth Institute of Nursing</td>
                  <td><strong>Account Number:</strong> 23880200000311</td>
                </tr>
                <tr>
                  <td><strong>Bank Name:</strong> FEDERAL BANK</td>
                  <td><strong>IFSC Code:</strong> FDRL0002388</td>
                </tr>
                <tr>
                  <td colSpan="2"><strong>Branch:</strong> Thanisandra, Bangalore, Karnataka</td>
                </tr>
              </tbody>
            </table>
            <div style={{ fontStyle: "italic", fontSize: "9.5px", marginTop: "4px", color: "#555" }}>
              <strong>Action Required:</strong> Upon remitting the payment, please immediately submit the UTR / Transaction Receipt to the Accounts Department to obtain your official Financial Clearance Slip and Examination Entry Pass.
            </div>
          </div>

          {/* Accounts Office Use */}
          <div className="office-box">
            <strong style={{ textTransform: "uppercase" }}>For Accounts Office Use Only</strong>
            <table style={{ width: "100%", marginTop: "4px" }}>
              <tbody>
                <tr>
                  <td>Amount Received: ___________</td>
                  <td>Receipt No.: ___________</td>
                  <td>Date: ____________</td>
                </tr>
                <tr>
                  <td style={{ paddingTop: "6px" }}><strong>Acc. Name/Sign:</strong> ___________</td>
                  <td colSpan="2" style={{ textAlign: "right", paddingTop: "6px" }}><strong>Principal Signature:</strong> ________________</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </Container>
  );
};

export default FeeDefaultNoticeLetter;